/**
 * Telegram Media Controller
 * 
 * Контроллер для загрузки медиа файлов (изображений и видео) из Telegram.
 * 
 * API Endpoint: POST /telegram/media
 * Body: { username: string, message_id: number }
 * 
 * Использование:
 * ```javascript
 * import TelegramMedia from '@/api/controllers/telegram-media'
 * 
 * const mediaData = await TelegramMedia.getTelegramMedia({
 *   username: 'velogidberlin',
 *   messageId: 6575
 * })
 * ```
 */

import module from '@/models/AxiosModule'

/**
 * Кеш для загруженных медиа файлов
 * Ключ: `tg_${username}_${messageId}`
 */
const MEDIA_CACHE = new Map()
const CACHE_EXPIRY_MS = 3600000 // 1 час

/**
 * Генерирует уникальный ключ для кеширования
 * @param {string} username - Username канала
 * @param {number|string} messageId - ID сообщения
 */
function generateCacheKey(username, messageId) {
  return `tg_${username}_${messageId}`
}

/**
 * Проверяет и возвращает кешированные данные
 * @param {string} key - Ключ кеша
 * @returns {Object|null} - Кешированные данные или null
 */
function getCachedMedia(key) {
  const cached = MEDIA_CACHE.get(key)
  if (!cached) return null
  
  const now = Date.now()
  if (now - cached.timestamp > CACHE_EXPIRY_MS) {
    MEDIA_CACHE.delete(key)
    return null
  }
  
  return cached.data
}

/**
 * Загружает медиа для Telegram поста
 * 
 * @param {Object} params - Параметры запроса
 * @param {string} params.username - Username канала Telegram
 * @param {number|string} params.messageId - ID сообщения (поста)
 * @returns {Promise<Object>} - Данные медиа от сервера
 */
export async function getTelegramMedia(params) {
  const { username, messageId } = params
  
  // Валидация обязательных параметров
  if (!username || !messageId) {
    throw new Error('Missing required parameters: username and messageId are required')
  }
  
  // Проверяем кеш
  const cacheKey = generateCacheKey(username, messageId)
  const cached = getCachedMedia(cacheKey)
  if (cached) {
    console.log(`[TG Media] Using cached media for ${cacheKey}`)
    return cached
  }
  
  console.log(`[TG Media] Fetching media for @${username}, message: ${messageId}`)
  
  try {
    const response = await module.Axios.post('/telegram/media', {
      username: String(username),
      message_id: Number(messageId)
    }, {
      timeout: 30000 // 30 секунд таймаут
    })
    
    const mediaData = response.data
    
    // Сохраняем в кеш
    MEDIA_CACHE.set(cacheKey, {
      data: mediaData,
      timestamp: Date.now()
    })
    
    console.log(`[TG Media] Successfully fetched media for @${username}, message: ${messageId}`)
    return mediaData
    
  } catch (error) {
    console.error('[TG Media] Request failed:', error)
    
    // Обработка ошибок
    if (error.response) {
      const { status, data } = error.response
      console.error(`[TG Media] Server error ${status}:`, data)
      
      if (status === 503) {
        throw new Error('Telegram сервис временно недоступен')
      } else if (status === 504) {
        throw new Error('Превышено время ожидания загрузки медиа')
      } else if (status === 401 || status === 403) {
        throw new Error('Ошибка авторизации')
      } else if (status === 404) {
        throw new Error('Медиа не найдено')
      } else {
        throw new Error(data?.error_message || data?.message || 'Ошибка загрузки медиа')
      }
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Превышено время ожидания запроса')
    } else {
      throw error
    }
  }
}

/**
 * Очищает кеш медиа (для освобождения памяти)
 */
export function clearMediaCache() {
  MEDIA_CACHE.clear()
  console.log('[TG Media] Cache cleared')
}

/**
 * Экспорт для использования в компонентах
 */
export default {
  getTelegramMedia,
  clearMediaCache
}

